import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { useCurrency } from "../../lib/currency";
import { Receipt, FolderKanban, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import ProfileCompletionCard from "../../components/ProfileCompletionCard";

export default function DashboardHome() {
  const { format } = useCurrency();
  const [data, setData] = useState({ orders: [], projects: [], invoices: [], jobs: [] });

  useEffect(() => {
    Promise.all([
      api.get("/orders/me").then(r=>r.data).catch(()=>[]),
      api.get("/projects/me").then(r=>r.data).catch(()=>[]),
      api.get("/invoices/me").then(r=>r.data).catch(()=>[]),
      api.get("/ai/jobs/me").then(r=>r.data).catch(()=>[]),
    ]).then(([orders, projects, invoices, jobs]) => setData({ orders, projects, invoices, jobs }));
  }, []);

  const stats = [
    { label: "Active Projects", value: data.projects.filter(p=>p.status==="in_progress").length, icon: FolderKanban },
    { label: "Total Orders", value: data.orders.length, icon: Receipt },
    { label: "Invoices Paid", value: data.invoices.length, icon: Receipt },
    { label: "AI Generations", value: data.jobs.length, icon: Sparkles },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-black tracking-tighter text-white" style={{fontFamily:'Outfit'}}>Overview</h1>
          <p className="text-gray-400">Snapshot of your account activity.</p>
        </div>
        <div className="flex gap-2">
          <Link to="/ai-builder" className="btn-primary !py-2 !px-4 text-sm">New AI Website</Link>
          <Link to="/ai-app-builder" className="btn-ghost !py-2 !px-4 text-sm">New AI App</Link>
        </div>
      </div>

      <ProfileCompletionCard/>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="dashboard-stats">
        {stats.map(s => (
          <div key={s.label} className="card p-5">
            <div className="flex items-center gap-2 text-cyan-400"><s.icon size={16}/> <span className="text-xs uppercase tracking-wider">{s.label}</span></div>
            <div className="text-3xl font-black text-white mt-2" style={{fontFamily:'Outfit'}}>{s.value}</div>
          </div>
        ))}
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        <div className="card p-5">
          <h3 className="text-white font-semibold mb-3">Recent Orders</h3>
          {data.orders.length === 0 ? <p className="text-gray-500 text-sm">No orders yet.</p> : (
            <ul className="space-y-2 text-sm">
              {data.orders.slice(0,5).map(o => (
                <li key={o.id} className="flex justify-between border-b border-white/5 py-2 last:border-0">
                  <div>
                    <div className="text-white">#{o.id.slice(0,8)}</div>
                    <div className="text-xs text-gray-500">{new Date(o.created_at).toLocaleDateString()}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-cyan-300">{format(o.total)}</div>
                    <div className={`text-xs ${o.status === "paid" ? "text-emerald-400" : "text-amber-400"}`}>{o.status}</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="card p-5">
          <h3 className="text-white font-semibold mb-3">Recent AI Jobs</h3>
          {data.jobs.length === 0 ? <p className="text-gray-500 text-sm">No AI generations yet.</p> : (
            <ul className="space-y-2 text-sm">
              {data.jobs.slice(0,5).map(j => (
                <li key={j.id} className="flex justify-between border-b border-white/5 py-2 last:border-0">
                  <div className="text-white">{j.type === "website" ? j.input.business_name : j.input.name}</div>
                  <div className="text-xs text-gray-500">{new Date(j.created_at).toLocaleDateString()}</div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
